<template>
  <div>
    <nav aria-label='breadcrumb'>
      <ol class='breadcrumb'>
        <li class='breadcrumb-item'>
          <a href='#'>{{translations('navbar.sale_management')}}</a>
        </li>
        <li class='breadcrumb-item'>
          <router-link to='/archive-sales'>{{translations('sale.archive_sale')}}</router-link>
        </li>
        <li class='breadcrumb-item active' aria-current='page'>{{translations('sale.shipping_detail')}}</li>
      </ol>
    </nav>
    <div class='container-fluid main-container'>
      <div class='d-flex flex-wrap'>
        <div class='left-container'>
          <h5 class='mb-3'>Information</h5>
          <table class='table table-sm table-borderless table-information'>
            <tbody>
              <tr>
                <th>{{translations('sale.order_date')}}</th>
                <td>{{formatDateAt(salesInfo.sold_at)}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.division')}}</th>
                <td><DivisionTypeText :division_type="salesInfo.division_type" :labelDefault="null" /></td>
              </tr>
              <tr>
                <th>{{translations('sale.order_number')}}</th>
                <td>{{salesInfo.sales_number}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.reserve_number')}}</th>
                <td>{{salesInfo.reserve_number ? salesInfo.reserve_number : '-'}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.ship_number')}}</th>
                <td>{{salesInfo.ship_number ? salesInfo.ship_number : '-'}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.supplier')}}</th>
                <td>
                  <span v-if="salesInfo.division_type !== 'B'">{{ salesInfo.client_name }}</span>
                  <span v-else>{{salesInfo.warehouse_name_from }}</span>
                </td>
              </tr>
              <tr>
                <th>{{translations('sale.destination_name')}}</th>
                <td>
                  <span v-if="salesInfo.division_type !== 'B'">{{ salesInfo.destination_name }}</span>
                  <span v-else>{{salesInfo.warehouse_name }}</span>
                </td>
              </tr>
              <tr>
                <th>{{translations('sale.sale_date')}}</th>
                <td>{{ formatDateAt(salesInfo.shipping_at) }}</td>
              </tr>
              <tr>
                <th>{{translations('sale.ship_date')}}</th>
                <td>{{ formatDateAt(salesInfo.shipping_schedule_at) }}</td>
              </tr>
              <tr>
                <th>{{translations('sale.closing_date')}}</th>
                <td>{{ formatDateAt(salesInfo.closing_at) }}</td>
              </tr>
              <tr>
                <th>{{translations('sale.contact_person')}}</th>
                <td>{{salesInfo.division_type === 'B' ? salesInfo.user_name : salesInfo.end_user}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.internal_memo')}}</th>
                <td style="white-space: break-spaces">{{salesInfo.note}}</td>
              </tr>
              <tr>
                <th>{{translations('sale.remarks')}}</th>
                <td>{{salesInfo.memo}}</td>
              </tr>
              <tr>
                <th>{{translations('common.last_updated_by')}}</th>
                <td>{{salesInfo.updated_by}}</td>
              </tr>
              <tr>
                <th>{{translations('common.last_update_time')}}</th>
                <td>{{ formatDateAt(salesInfo.updated_at) }}</td>
              </tr>
            </tbody>
          </table>
          <hr>
          <div class='form-inline'>
            <div class='form-group'>
              <a class='btn btn-link btn-sm text-danger' @click.self="deleteSales()" data-href='#'>{{translations('sale.delete')}}</a>
            </div>
          </div>
        </div>
        <div class='right-container'>
          <div class="form-inline justify-content-end"><!-- VAT -->
            <div v-if="taxIncluded" class="text-danger">{{translations('common.tax_included')}}</div><!-- TAXSTATUS -->
            <div v-else>{{translations('common.tax_excluded')}}</div>
          </div>
          <div class='table-scroll mb-4'>
            <table class='table table-sm table-striped table-800'>
              <thead>
                <tr>
                  <th></th>
                  <th>{{translations('sale.commodity_condition')}}</th>
                  <th>{{translations('sale.jan_code')}}</th>
                  <th>{{translations('sale.model')}}</th>
                  <th>{{translations('sale.tax_classification')}}</th>
                  <th>{{translations('sale.quantity')}}</th>
                  <th>{{translations('sale.unit_price')}}</th>
                  <th>{{translations('sale.client_order_number')}}</th>
                  <th>{{translations('sale.summary')}}</th>
                  <th>{{translations('sale.product_internal_memo')}}</th>
                  <th>{{translations('sale.total_amount')}}</th>
                  <th>{{translations('sale.total_cost')}}</th>
                  <th>{{translations('sale.gross_profit')}}</th>
                </tr>
              </thead>
              <tbody class="nowrap">
                <template v-for="(detail, i) in salesDetails">
                  <tr :key="i">
                    <td></td>
                    <td>{{ Number(detail.is_outlet) ? translations('sale.outlet') : Number(detail.is_bad) ? translations('sale.bad') : translations('sale.good') }}</td>
                    <td>{{ detail.jan_code }}</td>
                    <td>{{ detail.type_number }}</td>
                    <td>{{ Number(detail.taxclass_id) === 2 ? translations('order.not_applicable') : translations('order.taxable_item') }}</td>
                    <td class="right">{{ Number(detail.quantity).toLocaleString() }}</td>
                    <td class="right">{{ toTaxFormat(detail, 'unit_price') }}</td>
                    <td>{{ detail.client_order_number }}</td>
                    <td>{{ detail.remarks }}</td>
                    <td>
                      <textarea style="color: #6c757d" v-model="detail.internal_memo"></textarea>
                    </td>
                    <td class="right">{{ toTaxFormat(detail, 'total_unit_price') }}</td>
                    <td class="right">{{ toTaxFormat(detail, 'total_cost') }}</td>
                    <td class="right">{{ toTaxFormat(detail, 'total_profit') }}</td>
                  </tr>
                </template>
                <tr class='bg-lightgreen'>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td class='font-weight-bold right'>{{ displayGrandTotal(grandTotalUnitPrice, grandTotalUnitPriceIncTax) }}</td>
                  <td class='font-weight-bold right'>{{ displayGrandTotal(grandTotalCost, grandTotalCostIncTax) }}</td>
                  <td class='font-weight-bold right'>{{ displayGrandTotal(grandTotalProfit, grandTotalProfitIncTax) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class='form-inline'>
            <div class="form-group">
              <a class="btn btn-link btn-sm decoration-underline" href="javascript:;" @click="openDeliverySlip()">{{ translations('sale.delivery_slip_output') }}<i class="fas fa-external-link-alt"></i></a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <vue-element-loading :active='isLoading' :is-full-screen='true'/>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import DivisionTypeText from '@/components/orders/DivisionTypeText'
import { mapState } from 'vuex'

export default {
  name: 'SingleSales',
  props: {},
  components: {
    VueElementLoading,
    DivisionTypeText
  },

  data () {
    return {
      taxIncluded: false, // VAT
      salesID: 0,
      salesInfo: {},
      salesDetails: [],
      grandTotalUnitPrice: 0,
      grandTotalUnitPriceIncTax: 0,
      grandTotalCost: 0,
      grandTotalCostIncTax: 0,
      grandTotalProfit: 0,
      grandTotalProfitIncTax: 0,
      isLoading: false,
      deliveryFile: null
    }
  },

  created () {
    if (!this.$route.query.sales_id) {
      this.$router.replace('archive-sales')
    }

    this.isLoading = true
    this.salesID = Number(this.$route.query.sales_id)

    this.getSales().then(() => {
      this.calcGrandTotals()
      this.popoverButtons()
    }).finally(() => {
      this.isLoading = false
    })
  },

  computed: {
    ...mapState(['currentLanguage']),
    confirmationDeleteSaleMessage () {
      return this.translations('sale.delete_sale')
    },
    confirmButtonText () {
      return this.translations('common.yes')
    },
    cancelButtonText () {
      return this.translations('common.no')
    }
  },

  methods: {
    translations (key) {
      const language = require(`@/locales/${this.currentLanguage}.json`)
      const keys = key.split('.')
      let value = language

      for (const k of keys) {
        value = value[k]
        if (!value) break
      }

      return value || key
    },
    getSales () {
      return this.$http.post(this.$api_url.sales_details, { sales_id: this.salesID }).then(response => {
        const result = response.data
        if (result.status === true) {
          this.salesInfo = result.information
          this.taxIncluded = result.information.tax_included
          this.salesDetails = result.details
          this.salesDetails.forEach(detail => {
            detail = this.prepareDetails(detail)
          })
          return Promise.resolve()
        } else {
          this.$toastr.error(result.message)
          this.$router.push('/archive-sales')
        }
      }).catch(error => {
        console.log('SingleSales:getSales:catch(error): ', error)
        return Promise.reject(new Error(this.translations('sale.notice_failed')))
      })
    },

    openDeliverySlip () {
      this.isLoading = true
      this.$http.post(this.$api_url.sales_delivery_slip, { sales_id: this.salesID }).then(response => {
        const result = response.data
        if (result.status === true || result.slip) {
          const arrBuff = this.__b64ToArrayBuffer(result.slip.replace('data:application/pdf;base64,', ''))
          const deliveryFile = new Blob([arrBuff], { type: 'application/pdf' })
          const deliveryFileURL = URL.createObjectURL(deliveryFile)
          window.open(deliveryFileURL)
        } else {
          console.log('SingleSales:openDeliverySlip:status=false: ', result.message)
          this.$toastr.error(result.message)
        }
      }).catch(error => {
        console.log('SingleSales:openDeliverySlip:catch(error): ', error)
        this.$toastr.error(this.translations('sale.notice_failed'))
      }).finally(() => {
        this.isLoading = false
      })
    },

    formatDateAt (v, b = 0) {
      if (!v) return '-----------'
      if (v.indexOf('-') > 0) {
        const x = /-/gi
        return v.replace(x, '/')
      } else {
        if (b) return v.substr(0, 4) + '-' + v.substr(4, 2) + '-' + v.substr(6, 2)
        else if (v.indexOf('/') > 0) return v
        else return v.substr(0, 4) + '/' + v.substr(4, 2) + '/' + v.substr(6, 2)
      }
    },

    __b64ToArrayBuffer (base64) {
      let binaryString = window.atob(base64)
      let len = binaryString.length
      let bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes.buffer
    },

    popoverButtons () {
      window.$('button[data-toggle="popover-x"]').each(function () {
        let targetID = window.$(this).attr('data-target')
        window.$(this).popoverButton({
          target: targetID,
          trigger: 'click'
        })
      })
    },

    toTaxFormat (data, key) {
      const price = data[key] || 0

      if (data.taxclass_id && Number(data.taxclass_id) === 2) { // 税区分（1:課税対象, 2:対象外）
        if (key === 'unit_price') {
          data.unit_price = price
          data.unit_price_inctax = price
        }
        return Number(price).toLocaleString()
      }

      const taxIncluded = data.tax_included || this.taxIncluded
      const taxRate = Number(data.tax_rate || this.taxRate)
      const inctax = this.$util.getIncludeTax(price, taxRate)
      if (key === 'unit_price') {
        data.unit_price = price
        data.unit_price_inctax = inctax
      }
      if (!taxIncluded) { // 税込以外
        return `${Number(price).toLocaleString()} (${Number(inctax).toLocaleString()})`
      } else {
        return `${Number(inctax).toLocaleString()} (${Number(price).toLocaleString()})`
      }
    },

    prepareDetails (data, hasCost = false) {
      data.total_cost = data.quantity * data.row_price
      data.total_unit_price = data.quantity * data.unit_price
      data.total_profit = data.total_unit_price - data.total_cost

      if (data.taxclass_id && Number(data.taxclass_id) === 2) { // 税区分（1:課税対象, 2:対象外）
        data.total_cost_inctax = data.total_cost
        data.total_unit_price_inctax = data.total_unit_price
        data.total_profit_inctax = data.total_profit
      } else {
        data.total_cost_inctax = this.$util.getIncludeTax(data.total_cost, data.tax_rate)
        data.total_unit_price_inctax = this.$util.getIncludeTax(data.total_unit_price, data.tax_rate)
        data.total_profit_inctax = this.$util.getIncludeTax(data.total_profit, data.tax_rate)
      }

      return data
    },

    calcGrandTotals () {
      this.grandTotalUnitPrice = 0
      this.grandTotalUnitPriceIncTax = 0
      this.grandTotalCost = 0
      this.grandTotalCostIncTax = 0
      this.grandTotalProfit = 0
      this.grandTotalProfitIncTax = 0

      this.salesDetails.forEach(v => {
        this.grandTotalUnitPrice += Number(v.total_unit_price)
        this.grandTotalCost += Number(v.total_cost)
      })

      this.grandTotalProfit = this.grandTotalUnitPrice - this.grandTotalCost

      this.grandTotalUnitPriceIncTax = this.$util.getIncludeTax(this.grandTotalUnitPrice, this.taxRate)
      this.grandTotalCostIncTax = this.$util.getIncludeTax(this.grandTotalCost, this.taxRate)
      this.grandTotalProfitIncTax = this.grandTotalUnitPriceIncTax - this.grandTotalCostIncTax
    },

    displayGrandTotal (val, valIncTax) {
      if (!this.taxIncluded) { // 税抜
        return `${Number(val).toLocaleString()} (${Number(valIncTax).toLocaleString()})`
      } else {
        return `${Number(valIncTax).toLocaleString()} (${Number(val).toLocaleString()})`
      }
    },

    deleteSales () {
      let $this = this
      this.$dialog.confirm(this.confirmationDeleteSaleMessage, {
        okText: this.confirmButtonText,
        cancelText: this.cancelButtonText
      })
        .then(async () => {
          this.isLoading = true
          try {
            let del = await this.$http.post(this.$api_url.sales_delete, { sales_id: $this.salesID })
            let $data = this.$invoiceTools.prepareResult(del.data, '', 'SingleSales:deleteSales')
            if ($data.success === true) {
              this.$toastr.success(this.translations('sale.notice_delete_shipping_information_success'), this.translations('common.success'))
              if (del.data.reserve) {
                await this.$http.post(this.$api_url.reserves_detail, { orders_id: del.data.order_id })
              } else {
                await this.$http.post(this.$api_url.orders_detail, { orders_id: del.data.order_id })
              }
              this.$router.replace('archive-sales')
              setTimeout(() => { this.isLoading = false }, 1000)
            } else {
              this.$toastr.error($data.data, this.translations('common.error'))
            }
            $this.isLoading = false
          } catch (error) {
            $this.isLoading = false
            console.log('SingleSales:deleteSales:catch(error): ', error)
            this.$toastr.error(this.translations('sale.notice_failed'), this.translations('common.error'))
          }
        }).catch(() => { console.log('Clicked `Cancel`') })
    }
  }
}
</script>

<style scoped>
textarea {
  resize: both;
}
</style>
